import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "./TransitTimeTrendChart.css";

export function TransitTimeTrendChart(props) {
  var data = Array.isArray(props.data) ? props.data : [];
  var timePeriod = props.timePeriod || "weekly";

  var normalized = useMemo(function () {
    return data.map(function (r) {
      var hub = (r && (r.consigner_code || r.hub_location || r.warehouse)) || "";
      return {
        x_axis: r ? r.x_axis : "",
        hub: String(hub),
        avg_duration:
          r && typeof r.avg_duration === "number"
            ? r.avg_duration
            : parseFloat((r && r.avg_duration) || 0),
        count:
          r && typeof r.count === "number"
            ? r.count
            : parseFloat((r && r.count) || 0),
      };
    });
  }, [data]);

  var topHubs = useMemo(function () {
    return pickTopHubsByCount(normalized, 5);
  }, [normalized]);

  var chartData = useMemo(function () {
    return pivotTrendForRecharts(normalized, topHubs, timePeriod);
  }, [normalized, topHubs, timePeriod]);

  var colors = [
    "#82ca9d",
    "#ff7300",
    "#ff4d4f",
    "#1976d2",
    "#8dd1e1",
    "#8884d8",
    "#ffc658",
  ];

  if (!chartData.length || !topHubs.length) {
    return <div style={{ padding: 12 }}>No trend data</div>;
  }

  return (
    <div className="tttcRoot">
      <div className="tttcHeader">
        <div className="tttcTitle">Average Transit Time Trend</div>
      </div>

      <div className="tttcChart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" tick={{ fontSize: 10 }} height={30} />

            <YAxis
              tick={{ fontSize: 10 }}
              label={{
                value: "Hours",
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 10 },
              }}
            />

            <Tooltip
              formatter={function (value, name) {
                var v = typeof value === "number" ? value : parseFloat(value || 0);
                return [v.toFixed(2) + " hrs", name];
              }}
              contentStyle={{ fontSize: 10 }}
            />

            <Legend wrapperStyle={{ fontSize: 10 }} />

            {topHubs.map(function (hub, index) {
              return (
                <Line
                  key={hub}
                  type="monotone"
                  dataKey={hub}
                  stroke={colors[index % colors.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  name={hub}
                  connectNulls={true}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ---------------- Helpers ---------------- */

function pickTopHubsByCount(trendArr, limit) {
  var counts = {};
  for (var i = 0; i < trendArr.length; i++) {
    var r = trendArr[i];
    if (!r || !r.hub) continue;
    var hub = r.hub;
    var cnt = r.count || 0;
    if (!counts[hub]) counts[hub] = 0;
    counts[hub] += cnt > 0 ? cnt : 1;
  }

  var hubs = Object.keys(counts);
  hubs.sort(function (a, b) {
    return counts[b] - counts[a];
  });

  return hubs.slice(0, limit);
}

function pivotTrendForRecharts(trendArr, hubsToInclude, timePeriod) {
  var hubSet = {};
  hubsToInclude.forEach(function (h) {
    hubSet[h] = true;
  });

  var byPeriod = {};

  for (var j = 0; j < trendArr.length; j++) {
    var row = trendArr[j];
    if (!row) continue;

    var rawPeriod = row.x_axis;
    var hub = row.hub;

    if (rawPeriod === null || rawPeriod === undefined || rawPeriod === "") continue;
    if (!hub) continue;
    if (!hubSet[hub]) continue;

    var displayPeriod = formatPeriodLabel(rawPeriod, timePeriod);
    var sortValue = getSortValue(rawPeriod, timePeriod);

    if (!byPeriod[displayPeriod]) {
      byPeriod[displayPeriod] = {
        period: displayPeriod,
        sortValue: sortValue,
      };
    }

    byPeriod[displayPeriod][hub] = row.avg_duration;
  }

  var out = Object.keys(byPeriod).map(function (k) {
    return byPeriod[k];
  });

  out.sort(function (a, b) {
    return a.sortValue - b.sortValue;
  });

  var zeroRow = {
    period: "0",
    sortValue: 0,
  };

  hubsToInclude.forEach(function (hub) {
    zeroRow[hub] = 0;
  });

  out.unshift(zeroRow);

  return out;
}

function formatPeriodLabel(value, timePeriod) {
  var s = String(value || "").trim();
  var n = parseInt(s, 10);

  if (timePeriod === "weekly") {
    return "Week " + s;
  }

  if (timePeriod === "monthly") {
    return getMonthLabel(n);
  }

  if (timePeriod === "yearly") {
    return s;
  }

  return s;
}

function getSortValue(value, timePeriod) {
  var s = String(value || "").trim();
  var n = parseInt(s, 10);

  if (timePeriod === "weekly") {
    return isNaN(n) ? 999 : n;
  }

  if (timePeriod === "monthly") {
    return isNaN(n) ? 999 : n;
  }

  if (timePeriod === "yearly") {
    return isNaN(n) ? 9999 : n;
  }

  return isNaN(n) ? 999 : n;
}

function getMonthLabel(n) {
  if (n === 1) return "Jan";
  if (n === 2) return "Feb";
  if (n === 3) return "Mar";
  if (n === 4) return "Apr";
  if (n === 5) return "May";
  if (n === 6) return "Jun";
  if (n === 7) return "Jul";
  if (n === 8) return "Aug";
  if (n === 9) return "Sep";
  if (n === 10) return "Oct";
  if (n === 11) return "Nov";
  if (n === 12) return "Dec";
  return String(n || "");
}